import { useCallback, useReducer } from "react";

import { ImportTypeEnum } from "../../../services/data-file-import-data-service/Models";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import { IKeyValuePair } from "../../../common/models/KeyValuePair";


type DataFileImportListStateType = {
    viewOptionState: ViewOptionStateEnum;
    filterText: string;
    importType: ImportTypeEnum;
    runImportUIDs: IKeyValuePair<string, number>[];
    isDirty: boolean;
};

export const defaultState: DataFileImportListStateType = {
    viewOptionState: ViewOptionStateEnum.Default,
    filterText: '',
    importType: ImportTypeEnum.None,
    isDirty: false,
    runImportUIDs: []
};


enum DataFileImportListStateActionEnum {
    FilterTextChanged = 'DATAFILE_IMPORT_LIST_FILTER_TEXT_CHANGED',
    ImportTypeChanged = 'DATAFILE_IMPORT_LIST_IMPORT_TYPE_CHANGED',
    AddRunImportUIDChanged = 'DATAFILE_IMPORT_LIST_ADD_RUN_UID_CHANGED',
    RemoveRunImportUIDChanged = 'DATAFILE_IMPORT_LIST_REMOVE_RUN_UID_CHANGED',
    Reset = 'DATAFILE_IMPORT_LIST_RESET_CHANGED'
}


type DataFileImportListStateActionType = {
    type: DataFileImportListStateActionEnum.FilterTextChanged;
    payload: string;
} | {
    type: DataFileImportListStateActionEnum.ImportTypeChanged;
    payload: ImportTypeEnum;
} | {
    type: DataFileImportListStateActionEnum.AddRunImportUIDChanged;
    payload: IKeyValuePair<string, number>
} | {
    type: DataFileImportListStateActionEnum.RemoveRunImportUIDChanged;
    payload: IKeyValuePair<string, number>
} | {
    type: DataFileImportListStateActionEnum.Reset;
    payload: void;
};

const optionsChanged = (newState: DataFileImportListStateType, 
    defaultState: DataFileImportListStateType) => 
        newState.filterText !== defaultState.filterText ||
        newState.importType !== defaultState.importType;


function reducer(state: DataFileImportListStateType, action: DataFileImportListStateActionType): DataFileImportListStateType {

    const copy = { ...state };
    switch (action.type) {

        case DataFileImportListStateActionEnum.Reset:
            return {...defaultState,
                runImportUIDs: [...state.runImportUIDs]
            };

        case DataFileImportListStateActionEnum.FilterTextChanged:
            copy.filterText = action.payload;

            copy.viewOptionState = optionsChanged(copy, defaultState) ? 
                                    ViewOptionStateEnum.Changed : 
                                    ViewOptionStateEnum.Default;

            return copy;

        case DataFileImportListStateActionEnum.ImportTypeChanged:
            copy.importType = action.payload;

            copy.viewOptionState = optionsChanged(copy, defaultState) ? 
                                    ViewOptionStateEnum.Changed : 
                                    ViewOptionStateEnum.Default;

            return copy;

        case DataFileImportListStateActionEnum.AddRunImportUIDChanged:
            copy.runImportUIDs.push(action.payload);
            return copy;

        case DataFileImportListStateActionEnum.RemoveRunImportUIDChanged:
            copy.runImportUIDs = state.runImportUIDs.filter(o => o.Key !== action.payload.Key);
            return copy;
        
        default: 
            return {
                ...state
            };
    }
}

export default function useDataFileImportListState() {
    const [state, dispatch] = useReducer(reducer, defaultState);

    const setFilterText = useCallback((value: string) => dispatch({
        type: DataFileImportListStateActionEnum.FilterTextChanged,
        payload: value
    }), []);

    const setImportType = useCallback((value: ImportTypeEnum) => dispatch({
        type: DataFileImportListStateActionEnum.ImportTypeChanged,
        payload: value
    }), []);

    const addRunImportUID = useCallback((value: IKeyValuePair<string, number>) => dispatch({
        type: DataFileImportListStateActionEnum.AddRunImportUIDChanged,
        payload: value
    }), []);

    const removeRunImportUID = useCallback((value: IKeyValuePair<string, number>) => dispatch({
        type: DataFileImportListStateActionEnum.RemoveRunImportUIDChanged,
        payload: value
    }), []);
    

    const resetState = useCallback(() => dispatch({
        type: DataFileImportListStateActionEnum.Reset,
        payload: undefined
    }), []);


    return {
        state,
        setFilterText,
        setImportType,
        addRunImportUID,
        removeRunImportUID,
        resetState
    };
}    
